import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { useApp } from "@/context/AppContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1602867741746-6df80f40b3f6?crop=entropy&cs=srgb&fm=jpg&q=85";

export const Hero = () => {
  const { content, t, wa, lang } = useApp();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.04, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
  const markY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const markRotate = useTransform(scrollYProgress, [0, 1], [0, 14]);
  const hero = content.hero;

  const scrollToProducts = () => {
    if (window.__lenis) window.__lenis.scrollTo("#komoditas", { offset: -70 });
  };

  return (
    <section ref={ref} id="beranda" data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-[#14281E]">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Lahan Yutaka Agro" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#14281E]/95 via-[#1E3A2B]/70 to-[#1E3A2B]/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#14281E]/80 via-transparent to-transparent" />
      </motion.div>

      <motion.div
        style={{ y: markY, rotate: markRotate }}
        className="pointer-events-none absolute z-[5] right-[-12%] top-[8%] w-[70vw] sm:right-[-4%] sm:top-[6%] sm:w-[52vw] lg:right-[4%] lg:top-[4%] lg:w-[46vw] max-w-[820px]"
        data-testid="hero-watermark"
      >
        <motion.img
          src="/logo-mark.png"
          alt=""
          aria-hidden="true"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 0.18, scale: [1, 1.04, 1], y: [0, -18, 0] }}
          transition={{
            opacity: { duration: 1.6, delay: 0.6 },
            scale: { duration: 12, repeat: Infinity, ease: "easeInOut" },
            y: { duration: 9, repeat: Infinity, ease: "easeInOut" },
          }}
          className="w-full h-auto mix-blend-luminosity saturate-50 select-none drop-shadow-[0_0_60px_rgba(229,184,105,0.25)]"
        />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 flex min-h-screen flex-col justify-center px-4 sm:px-8 lg:px-16 pt-28 pb-40">
        <div className="overflow-hidden mb-6">
          <motion.p
            initial={{ y: "120%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.28em] text-[#E5B869]"
            data-testid="hero-eyebrow"
          >
            {t(hero.eyebrow)}
          </motion.p>
        </div>

        <h1 className="font-serif font-bold text-[#FAF7F2] text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl" data-testid="hero-headline">
          {hero.lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.95, delay: 0.3 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                className={`block ${i === hero.lines.length - 1 ? "italic text-[#D96B27]" : ""}`}
              >
                {t(line)}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
          className="mt-7 max-w-xl text-base sm:text-lg text-[#FAF7F2]/75 leading-relaxed"
          data-testid="hero-subtext"
        >
          {t(hero.sub)}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={scrollToProducts}
            className="group flex items-center gap-3 px-7 py-3.5 rounded-full bg-[#D96B27] text-[#FAF7F2] text-sm font-semibold tracking-wide hover:bg-[#FAF7F2] hover:text-[#1E3A2B] transition-colors duration-300"
          >
            {t(hero.cta_primary)}
            <ArrowDown size={15} className="transition-transform duration-300 group-hover:translate-y-0.5" />
          </button>
          <a
            data-testid="hero-cta-whatsapp"
            href={wa(lang === "id" ? "Halo Yutaka Agro, saya ingin bertanya tentang produk Anda." : "Hello Yutaka Agro, I would like to ask about your products.")}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-7 py-3.5 rounded-full border border-[#FAF7F2]/40 text-[#FAF7F2] text-sm font-semibold tracking-wide hover:bg-[#FAF7F2]/10 hover:border-[#FAF7F2] transition-colors duration-300"
          >
            <MessageCircle size={15} />
            {t(hero.cta_secondary)}
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.3 }}
        className="absolute bottom-0 left-0 right-0 z-10 grid grid-cols-2 md:grid-cols-4 border-t border-[#FAF7F2]/15 bg-[#14281E]/40 backdrop-blur-md"
        data-testid="hero-stats"
      >
        {content.stats.map((s, i) => (
          <div key={i} className="px-5 sm:px-8 py-5 border-r last:border-r-0 border-[#FAF7F2]/10" data-testid={`hero-stat-${i}`}>
            <p className="font-serif text-2xl sm:text-3xl font-semibold text-[#E5B869]">{s.value}</p>
            <p className="mt-1 text-[10px] font-mono uppercase tracking-[0.18em] text-[#FAF7F2]/55">{t(s.label)}</p>
          </div>
        ))}
      </motion.div>
    </section>
  );
};
